import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Chip,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useTranslation } from "react-i18next";
import {
  getPlatformDisplayName,
  getPlatformIconPath,
} from "../../utils/iconUtils";
import { components } from "../../types/api";
import OrbLoader from "../common/OrbLoader";
import { getStatusDisplay } from "../../utils/statusUtilts";
import SyncErrorBadge from "./SyncErrorBadge";
import {
  createNumberFormat,
  formatPercentValue,
} from "../../utils/numberFormat";

// Define the types based on the schema
interface InternalCampaignMetrics {
  impressions: number;
  clicks: number;
  ctr: number;
  cpm: number;
  cpc: number;
  cost: number;
  realCost?: number; // Real cost field - only shown to admins
  cpv?: number;
  vtr?: number;
  dailyInvestment?: number;
  dailyImpressions?: number;
  completeViews?: number;
  dailyCTR?: number;
  conversions?: number;
  date?: string;
}

interface InternalCampaign {
  id: string;
  displayName: string;
  internalPlatform?: string;
  platform?: string;
  internalId?: string;
  margin?: number;
  startDate?: string | null;
  endDate?: string | null;
  timezone?: string;
  status?: components["schemas"]["CampaignStatus"];
  metrics?: InternalCampaignMetrics;
  errorSync?: string | null;
  lastSyncAt?: string | null;
  parentMergeId?: string;
  entityType?: "MERGE" | "AD_ORDER";
  insertionOrderId?: string;
}

interface InternalCampaignMetricsTableProps {
  internalCampaigns: InternalCampaign[];
  isLoading: boolean;
  maxHeight?: string | number;
  stickyHeader?: boolean;
  isAdmin?: boolean; // Flag to indicate if the user is an admin
  canViewInsertionOrderId?: boolean; // Clients shouldn't see the Insertion Order ID
  onInternalCampaignClick?: (internalCampaign: InternalCampaign) => void;
  onInternalCampaignOpenInNewTab?: (internalCampaign: InternalCampaign) => void;
  onExport?: () => void;
  exportDisabled?: boolean;
}

const InternalCampaignMetricsTable: React.FC<
  InternalCampaignMetricsTableProps
> = ({
  internalCampaigns,
  isLoading,
  maxHeight = 400,
  stickyHeader = true,
  isAdmin = false,
  canViewInsertionOrderId = true,
  onInternalCampaignClick,
  onInternalCampaignOpenInNewTab,
  onExport,
  exportDisabled = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [contextMenu, setContextMenu] = React.useState<{
    mouseX: number;
    mouseY: number;
    internalCampaign: InternalCampaign;
  } | null>(null);

  const handleRowContextMenu = (
    e: React.MouseEvent,
    internalCampaign: InternalCampaign
  ) => {
    if (!onInternalCampaignOpenInNewTab) return;
    e.preventDefault();
    setContextMenu({
      mouseX: e.clientX,
      mouseY: e.clientY,
      internalCampaign,
    });
  };

  const handleCloseContextMenu = () => setContextMenu(null);

  const handleOpenInNewTab = () => {
    if (contextMenu) {
      onInternalCampaignOpenInNewTab?.(contextMenu.internalCampaign);
    }
    handleCloseContextMenu();
  };

  const showStatusColumn = internalCampaigns.some((internalCampaign) =>
    Boolean(internalCampaign.status)
  );
  const showSyncColumns =
    isAdmin &&
    internalCampaigns.some(
      (internalCampaign) =>
        Boolean(internalCampaign.lastSyncAt) ||
        Boolean(internalCampaign.errorSync)
    );
  const showEntityTypeColumn = internalCampaigns.some((campaign) =>
    Boolean(campaign.entityType)
  );
  const showInsertionOrderColumn =
    canViewInsertionOrderId &&
    internalCampaigns.some(
      (campaign) =>
        campaign.entityType === "AD_ORDER" && campaign.insertionOrderId
    );

  // Format numbers for display
  const formatNumber = (num: number | undefined): string => {
    if (num === undefined || isNaN(num)) return "-";
    return createNumberFormat().format(Math.round(num));
  };

  // Format currency
  const formatCurrency = (num: number | undefined): string => {
    if (num === undefined || isNaN(num)) return "-";
    return createNumberFormat({
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  // Format percentage
  const formatPercentage = (num: number | undefined): string => {
    if (num === undefined || isNaN(num)) return "-";
    return formatPercentValue(num * 100);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <OrbLoader
          size="large"
          label={t("common.loading", { defaultValue: "Cargando…" })}
        />
      </Box>
    );
  }

  if (!internalCampaigns || internalCampaigns.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          {t("campaigns.noInternalCampaigns", {
            defaultValue: "No internal campaigns available for this campaign",
          })}
        </Typography>
      </Box>
    );
  }

  const headerCellSx = {
    fontWeight: 700,
    fontSize: "11px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.04em",
    color: theme.palette.text.secondary,
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: 2,
        maxHeight: maxHeight,
        overflow: "auto",
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Table stickyHeader={stickyHeader}>
        <TableHead
          sx={{
            backgroundColor: theme.palette.background.paper,
            "& th": {
              backgroundColor: theme.palette.background.paper,
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          <TableRow>
            <TableCell sx={headerCellSx}>
              {t("campaigns.internalCampaignColumn", {
                defaultValue: "Internal Campaign",
              })}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("campaigns.platform", { defaultValue: "Platform" })}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("campaigns.impressions", { defaultValue: "Impressions" })}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("campaigns.clicks", { defaultValue: "Clicks" })}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("campaigns.ctr", { defaultValue: "CTR" })}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("campaigns.cpm", { defaultValue: "CPM" })}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("campaigns.cpc", { defaultValue: "CPC" })}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("campaigns.cost", { defaultValue: "Cost" })}
            </TableCell>
            {isAdmin && (
              <TableCell sx={headerCellSx}>
                {t("campaigns.realCost", { defaultValue: "Real Cost" })}
              </TableCell>
            )}
            {showStatusColumn && (
              <TableCell sx={headerCellSx}>
                {t("campaigns.status", { defaultValue: "Status" })}
              </TableCell>
            )}
            {showEntityTypeColumn && (
              <TableCell sx={headerCellSx}>
                {t("campaigns.entityType", { defaultValue: "Type" })}
              </TableCell>
            )}
            {showInsertionOrderColumn && (
              <TableCell sx={headerCellSx}>
                {t("campaigns.insertionOrderId", {
                  defaultValue: "Insertion Order ID",
                })}
              </TableCell>
            )}
            {showSyncColumns && (
              <>
                <TableCell sx={headerCellSx}>
                  {t("campaigns.lastSyncAt", { defaultValue: "Last Sync" })}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  {t("campaigns.errorSync", { defaultValue: "Sync Error" })}
                </TableCell>
              </>
            )}
            {onExport && (
              <TableCell sx={{ width: 40, p: 0.5, textAlign: "center" }}>
                <Tooltip
                  title={t("campaigns.exportMetrics", {
                    defaultValue: "Export Data",
                  })}
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={onExport}
                      disabled={exportDisabled}
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      <FileDownloadIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {internalCampaigns.map((internalCampaign, index) => (
            <TableRow
              key={internalCampaign.id}
              onClick={() => onInternalCampaignClick?.(internalCampaign)}
              onContextMenu={(e) => handleRowContextMenu(e, internalCampaign)}
              hover
              sx={{
                cursor: onInternalCampaignClick ? "pointer" : "default",
                backgroundColor:
                  index % 2 === 1
                    ? theme.palette.action.hover
                    : theme.palette.background.paper,
                "& td": {
                  borderBottom: `1px solid ${theme.palette.divider}`,
                },
                "&:hover": {
                  backgroundColor: theme.palette.action.selected,
                },
              }}
            >
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 24, height: 24, flexShrink: 0 }}>
                    <img
                      src={getPlatformIconPath(
                        internalCampaign.platform ||
                          internalCampaign.internalPlatform ||
                          "-"
                      )}
                      alt={getPlatformDisplayName(
                        internalCampaign.platform ||
                          internalCampaign.internalPlatform ||
                          "-"
                      )}
                      width="100%"
                      height="100%"
                    />
                  </Box>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {internalCampaign.displayName ||
                      t("campaigns.internalCampaign", {
                        defaultValue: "Internal Campaign",
                      })}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                {getPlatformDisplayName(
                  internalCampaign.platform ||
                    internalCampaign.internalPlatform ||
                    "-"
                )}
              </TableCell>
              <TableCell>
                {internalCampaign.metrics
                  ? formatNumber(internalCampaign.metrics.impressions)
                  : "-"}
              </TableCell>
              <TableCell>
                {internalCampaign.metrics
                  ? formatNumber(internalCampaign.metrics.clicks)
                  : "-"}
              </TableCell>
              <TableCell>
                {internalCampaign.metrics
                  ? formatPercentage(internalCampaign.metrics.ctr)
                  : "-"}
              </TableCell>
              <TableCell>
                {internalCampaign.metrics
                  ? formatCurrency(internalCampaign.metrics.cpm)
                  : "-"}
              </TableCell>
              <TableCell>
                {internalCampaign.metrics
                  ? formatCurrency(internalCampaign.metrics.cpc)
                  : "-"}
              </TableCell>
              <TableCell>
                {internalCampaign.metrics
                  ? formatCurrency(internalCampaign.metrics.cost)
                  : "-"}
              </TableCell>
              {isAdmin && (
                <TableCell>
                  {internalCampaign.metrics
                    ? formatCurrency(internalCampaign.metrics.realCost || 0)
                    : "-"}
                </TableCell>
              )}
              {showStatusColumn && (
                <TableCell>
                  {internalCampaign.status ? (
                    (() => {
                      const statusDisplay = getStatusDisplay(
                        t,
                        internalCampaign.status
                      );
                      return (
                        <Chip
                          label={statusDisplay.label}
                          size="small"
                          color={statusDisplay.color}
                          variant="outlined"
                        />
                      );
                    })()
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {t("common.notAvailable", { defaultValue: "N/A" })}
                    </Typography>
                  )}
                </TableCell>
              )}
              {showEntityTypeColumn && (
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {internalCampaign.entityType === "AD_ORDER"
                      ? t("campaigns.entityTypeAdOrder", {
                          defaultValue: "Ad Order",
                        })
                      : t("campaigns.entityTypeMerge", {
                          defaultValue: "Merged Group",
                        })}
                  </Typography>
                </TableCell>
              )}
              {showInsertionOrderColumn && (
                <TableCell>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {internalCampaign.entityType === "AD_ORDER"
                      ? internalCampaign.insertionOrderId || "-"
                      : "-"}
                  </Typography>
                </TableCell>
              )}
              {showSyncColumns && (
                <>
                  <TableCell>
                    <Typography variant="caption">
                      {internalCampaign.lastSyncAt
                        ? dayjs(internalCampaign.lastSyncAt).format(
                            "DD MMM YYYY HH:mm"
                          )
                        : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <SyncErrorBadge error={internalCampaign.errorSync} />
                  </TableCell>
                </>
              )}
              {onExport && <TableCell sx={{ width: 40, p: 0 }} />}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleOpenInNewTab}>
          <OpenInNewIcon fontSize="small" sx={{ mr: 1 }} />
          {t("campaigns.filters.openInNewTab", {
            defaultValue: "Abrir en nueva pestaña",
          })}
        </MenuItem>
      </Menu>
    </TableContainer>
  );
};

export default InternalCampaignMetricsTable;
